import { createServer } from "node:http";
import type { AddressInfo } from "node:net";

export interface OAuthCallbackResult {
  code: string;
  state: string;
}

export interface LocalAuthServerOptions {
  /** Preferred port; falls back through preferredPort+1..+9 until one binds. */
  preferredPort: number;
  /** Expected state value — rejects callbacks with mismatched state. */
  expectedState: string;
  /** Milliseconds to wait before timing out. Default: 5 minutes. */
  timeoutMs?: number;
  /** Path to listen on. Default: /oauth/callback */
  callbackPath?: string;
  /** HTML to show in the browser on success. */
  successHtml?: string;
  /** HTML to show in the browser on error. */
  errorHtml?: string;
}

const DEFAULT_SUCCESS_HTML = `<!DOCTYPE html>
<html>
<head><title>OrgX connected</title>
<style>body{font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;background:#f9fafb}
.card{text-align:center;padding:2rem;max-width:400px}
h1{font-size:1.5rem;color:#111}p{color:#555;margin-top:.5rem}</style>
</head>
<body><div class="card">
<h1>&#10003; Connected to OrgX</h1>
<p>You can close this tab and return to your terminal.</p>
</div></body></html>`;

const DEFAULT_ERROR_HTML = `<!DOCTYPE html>
<html>
<head><title>OrgX auth error</title>
<style>body{font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;background:#fef2f2}
.card{text-align:center;padding:2rem;max-width:400px}
h1{font-size:1.5rem;color:#b91c1c}p{color:#555;margin-top:.5rem}</style>
</head>
<body><div class="card">
<h1>&#10007; Authentication failed</h1>
<p>Return to your terminal and try again.</p>
</div></body></html>`;

function tryListen(port: number, hostname: string): Promise<ReturnType<typeof createServer>> {
  return new Promise((resolve, reject) => {
    const server = createServer();
    server.once("error", reject);
    server.listen(port, hostname, () => {
      server.removeListener("error", reject);
      resolve(server);
    });
  });
}

async function bindServer(
  preferredPort: number,
  hostname: string,
): Promise<{ server: ReturnType<typeof createServer>; port: number }> {
  // Port 0 lets the OS pick a free port — read the actual port back from address()
  if (preferredPort === 0) {
    const server = await tryListen(0, hostname);
    const port = (server.address() as AddressInfo).port;
    return { server, port };
  }

  for (let offset = 0; offset < 10; offset++) {
    const port = preferredPort + offset;
    try {
      const server = await tryListen(port, hostname);
      return { server, port };
    } catch {
      // port busy — try next
    }
  }
  throw new Error(
    `Could not bind local auth server: ports ${preferredPort}–${preferredPort + 9} are all in use.`,
  );
}

/**
 * Starts a local HTTP server that waits for a single OAuth callback redirect,
 * validates the state parameter, and returns the authorization code.
 *
 * Returns the bound port so callers can build the redirect_uri before opening
 * the browser.
 */
export async function startLocalAuthServer(
  options: LocalAuthServerOptions,
): Promise<{ port: number; result: Promise<OAuthCallbackResult> }> {
  const hostname = "127.0.0.1";
  const callbackPath = options.callbackPath ?? "/oauth/callback";
  const timeoutMs = options.timeoutMs ?? 5 * 60_000;
  const successHtml = options.successHtml ?? DEFAULT_SUCCESS_HTML;
  const errorHtml = options.errorHtml ?? DEFAULT_ERROR_HTML;

  const { server, port } = await bindServer(options.preferredPort, hostname);

  const result = new Promise<OAuthCallbackResult>((resolve, reject) => {
    const timer = setTimeout(() => {
      server.close();
      reject(new Error("Timed out waiting for browser authorization."));
    }, timeoutMs);

    server.on("request", (req, res) => {
      if (!req.url) {
        res.writeHead(400).end(errorHtml);
        return;
      }

      const url = new URL(req.url, `http://${hostname}:${port}`);

      if (url.pathname !== callbackPath) {
        res.writeHead(404).end("Not found");
        return;
      }

      const error = url.searchParams.get("error");
      const errorDescription = url.searchParams.get("error_description");

      if (error) {
        res.writeHead(400, { "Content-Type": "text/html" }).end(errorHtml);
        clearTimeout(timer);
        server.close();
        reject(new Error(errorDescription ?? error));
        return;
      }

      const code = url.searchParams.get("code");
      const state = url.searchParams.get("state");

      if (!code || !state) {
        res.writeHead(400, { "Content-Type": "text/html" }).end(errorHtml);
        clearTimeout(timer);
        server.close();
        reject(new Error("Missing code or state in callback."));
        return;
      }

      if (state !== options.expectedState) {
        res.writeHead(400, { "Content-Type": "text/html" }).end(errorHtml);
        clearTimeout(timer);
        server.close();
        reject(new Error("State mismatch — possible CSRF. Retry auth."));
        return;
      }

      res.writeHead(200, { "Content-Type": "text/html" }).end(successHtml);
      clearTimeout(timer);
      server.close();
      resolve({ code, state });
    });
  });

  return { port, result };
}

export function buildRedirectUri(port: number, callbackPath = "/oauth/callback"): string {
  return `http://127.0.0.1:${port}${callbackPath}`;
}

/** Returns the actual port the server is listening on (useful if OS assigned it). */
export function getServerPort(server: ReturnType<typeof createServer>): number {
  return (server.address() as AddressInfo).port;
}
